import React, {useCallback, useMemo, useState} from "react";
import {createColumnHelper} from "@tanstack/react-table";
import DataGrid from "../DataGrid/DataGrid.jsx";
import {ABC_COLORS, formatEuro, formatMonthPeriod, formatNumber, formatPercent, OTHER_GROUP_COLOR} from "./format.js";

const columnHelper = createColumnHelper();

/**
 * Baut die Basis-URL mit den gemeinsamen Filtern der Seite als feste
 * Query-Parameter. DataGrid selbst kennt diese Filter nicht (Suche,
 * Sortierung, Paging sind reine DataGrid-Zustände) - über die bereits in der
 * URL enthaltene Query ergänzt axios beim internen Abruf einfach weitere
 * Parameter (Suche/Sortierung/Seite), ohne dass DataGrid angepasst werden
 * müsste.
 */
function withQuery(baseUrl, filters) {
    const params = new URLSearchParams();
    if (filters.date_from) {
        params.set("date_from", filters.date_from);
    }
    if (filters.date_to) {
        params.set("date_to", filters.date_to);
    }
    if (filters.level) {
        params.set("level", filters.level);
    }
    (filters.article_groups || []).forEach((group) => params.append("article_groups[]", group));
    if (filters.search) {
        params.set("search", filters.search);
    }
    const query = params.toString();
    return query ? `${baseUrl}?${query}` : baseUrl;
}

/**
 * #11 Detailtabelle je Artikel - strukturgleich zur Bestandsübersicht
 * (Stock/Overview.jsx): serverseitiges Paging/Sortieren über DataGrid,
 * Excel-Export, Spaltenfilter (meta.filterVariant) und Gruppierung nach
 * Warengruppe.
 *
 * Der Statistik-Backend-Endpunkt (SalesStatisticsController::data()/
 * groups()) wertet dafür wie StockOverviewController filter[<spalte>]-
 * Parameter aus; der Gruppen-Nachladepfad von DataGrid sendet
 * filter[article_group]=<Wert> als exakten Gleichheitsfilter, den
 * SalesStatisticsQuery::applyColumnFilters() genauso auswertet wie
 * StockOverviewQuery die Warengruppe (inkl. "__none__" für "Ohne
 * Warengruppe"). Der Antwort-Feldname der Gruppenliste ist bewusst
 * "warengruppe" (nicht "article_group"), weil DataGrid.jsx diesen Feldnamen
 * im Gruppierungsmodus fest erwartet.
 *
 * Das eingebaute Suchfeld von DataGrid bleibt als zusätzliche Verfeinerung
 * innerhalb der Detailtabelle erhalten; bei Eingabe überschreibt es (nur für
 * diese Tabelle) den globalen Suchbegriff aus der Filterleiste.
 *
 * Auswertungsebene (filters.level, siehe FilterBar.jsx): Bei "parent" liefert
 * der Backend-Endpunkt bereits eine Zeile je Übernummer (article_number IST
 * dann die Übernummer, siehe SalesStatisticsQuery::articleAggregateRows()) -
 * die Tabelle braucht dafür keine eigene Umrechnung, nur andere Spaltentexte
 * ("Übernummer" statt "Artikelnummer"). Die separate Spalte "Übernummer"
 * bleibt auf beiden Ebenen sichtbar: auf Artikel-Ebene zeigt sie, zu welcher
 * Übernummer ein Artikel gehört (filter- und gruppierbar über die neuen
 * groups-by-parent-Endpunkte), auf Übernummer-Ebene dupliziert sie zwangsläufig
 * die führende Spalte.
 */
export default function DetailDataGrid({filters, groupOptions = [], level = "article"}) {
    const dataUrl = useMemo(() => withQuery("/statistics/data", filters), [filters]);
    const exportUrl = useMemo(() => withQuery("/statistics/export", filters), [filters]);
    // Der Gruppen-Endpunkt braucht dieselben festen Query-Parameter wie
    // dataUrl/exportUrl - sonst würde die Gruppierung beim Wechsel des
    // Zeitraum-/Warengruppen-Filters der Filterleiste weiterhin auf dem alten
    // (oder gar keinem) Zeitraum zählen.
    const groupsUrl = useMemo(() => withQuery("/statistics/groups", filters), [filters]);
    const groupsByParentUrl = useMemo(() => withQuery("/statistics/groups-by-parent", filters), [filters]);
    const groupingOptions = useMemo(
        () => [
            {
                id: "article_group",
                label: "Warengruppe",
                columnId: "article_group",
                groupsUrl,
                groupField: "warengruppe",
                noneLabel: "Ohne Warengruppe",
                pluralLabel: "Warengruppen",
            },
            {
                id: "parent_article_number",
                label: "Übernummer",
                columnId: "parent_article_number",
                groupsUrl: groupsByParentUrl,
                groupField: "parent_article_number",
                noneLabel: "Ohne Übernummer",
                pluralLabel: "Übernummern",
            },
        ],
        [groupsUrl, groupsByParentUrl]
    );

    // Die Sortierung bleibt über einen Filterwechsel hinweg erhalten, obwohl
    // DataGrid unten per key neu gemountet wird (siehe dort) - dafür merkt
    // sich diese Seite die zuletzt gewählte Sortierung selbst.
    const [initialSorting, setInitialSorting] = useState([{id: "net_amount", desc: true}]);
    const handleStateChange = useCallback((state) => {
        if (Array.isArray(state?.sorting) && state.sorting.length) {
            setInitialSorting(state.sorting);
        }
    }, []);

    const columns = useMemo(
        () => [
            columnHelper.accessor("article_number", {
                id: "article_number",
                header: level === "parent" ? "Übernummer" : "Artikelnummer",
                meta: {
                    width: 150,
                    filterVariant: "text",
                    help:
                        level === "parent"
                            ? "Übernummer (Sammelartikel über allen Farbvarianten) - auf dieser Ebene bereits die führende Zeile."
                            : "Artikelnummer (SKU).",
                },
            }),
            columnHelper.accessor("name", {
                id: "name",
                header: "Bezeichnung",
                cell: (info) => info.getValue() || "–",
                meta: {width: 280, filterVariant: "text", help: "Artikelbezeichnung."},
            }),
            columnHelper.accessor("parent_article_number", {
                id: "parent_article_number",
                header: "Übernummer",
                cell: (info) => info.getValue() || "–",
                meta: {
                    width: 130,
                    filterVariant: "text",
                    help: "Übernummer (Sammelartikel über allen Farbvarianten dieses Artikels) - Grundlage der Buchungsumstellung 2020/2021, siehe Hinweis in der Filterleiste.",
                },
            }),
            columnHelper.accessor("parent_name", {
                id: "parent_name",
                header: "Übernummer-Bezeichnung",
                cell: (info) => info.getValue() || "–",
                meta: {width: 220, filterVariant: "text", help: "Bezeichnung der Übernummer."},
            }),
            columnHelper.accessor("article_group", {
                id: "article_group",
                header: "Warengruppe",
                cell: (info) => info.getValue() || "–",
                meta: {
                    width: 150,
                    filterVariant: "select",
                    selectOptions: [
                        {value: "", label: "Alle"},
                        {value: "__none__", label: "– (ohne Warengruppe)"},
                        ...groupOptions,
                    ],
                    help: "Warengruppe des Artikels. Über den Filter-Schalter oben lässt sich die Tabelle nach dieser Spalte gruppieren.",
                },
            }),
            columnHelper.accessor("quantity", {
                id: "quantity",
                header: "Menge",
                cell: (info) => formatNumber(info.getValue()),
                meta: {width: 110, filterVariant: "number", help: "Verkaufte Menge im gewählten Zeitraum, Retouren bereits gegengerechnet."},
            }),
            columnHelper.accessor("net_amount", {
                id: "net_amount",
                header: "Nettoumsatz",
                cell: (info) => formatEuro(info.getValue()),
                meta: {width: 130, filterVariant: "number", help: "Nettoumsatz im gewählten Zeitraum."},
            }),
            columnHelper.accessor("line_count", {
                id: "line_count",
                header: "Positionen",
                cell: (info) => formatNumber(info.getValue()),
                meta: {width: 110, filterVariant: "number", help: "Anzahl der Belegpositionen im gewählten Zeitraum."},
            }),
            columnHelper.accessor("first_sale_month", {
                id: "first_sale_month",
                header: "Erster Verkauf",
                cell: (info) => formatMonthPeriod(info.getValue()),
                meta: {width: 120, filterVariant: "text", help: "Erster Monat mit Umsatz im gewählten Zeitraum. Filter sucht im Format JJJJ-MM, z. B. \"2025\" oder \"2025-03\"."},
            }),
            columnHelper.accessor("last_sale_month", {
                id: "last_sale_month",
                header: "Letzter Verkauf",
                cell: (info) => formatMonthPeriod(info.getValue()),
                meta: {width: 120, filterVariant: "text", help: "Letzter Monat mit Umsatz im gewählten Zeitraum. Filter sucht im Format JJJJ-MM, z. B. \"2025\" oder \"2025-03\"."},
            }),
            columnHelper.accessor("revenue_share_pct", {
                id: "revenue_share_pct",
                header: "Umsatzanteil",
                cell: (info) => formatPercent(info.getValue()),
                meta: {width: 115, filterVariant: "number", help: "Anteil dieses Artikels am gesamten positiven Nettoumsatz aller gefilterten Artikel."},
            }),
            columnHelper.accessor("cumulative_share_pct", {
                id: "cumulative_share_pct",
                header: "Kumuliert",
                cell: (info) => formatPercent(info.getValue()),
                meta: {width: 110, help: "Kumulierter Umsatzanteil bis einschließlich dieses Artikels (absteigend nach Umsatz) - Grundlage der ABC-Klasse."},
            }),
            columnHelper.accessor("abc_class", {
                id: "abc_class",
                header: "ABC",
                cell: (info) => {
                    const value = info.getValue();
                    return value ? (
                        <span className="badge" style={{backgroundColor: ABC_COLORS[value] || OTHER_GROUP_COLOR}}>{value}</span>
                    ) : "–";
                },
                meta: {
                    width: 70,
                    filterVariant: "select",
                    selectOptions: [
                        {value: "", label: "Alle"},
                        {value: "A", label: "A"},
                        {value: "B", label: "B"},
                        {value: "C", label: "C"},
                    ],
                    help: "A bis 80 % kumuliertem Umsatzanteil, B bis 95 %, C der Rest.",
                },
            }),
            columnHelper.accessor("revenue_rank", {
                id: "revenue_rank",
                header: "Rang",
                cell: (info) => formatNumber(info.getValue()),
                meta: {width: 90, help: "Rang nach Nettoumsatz innerhalb der gefilterten Artikel (1 = höchster Umsatz)."},
            }),
        ],
        [groupOptions, level]
    );

    return (
        <DataGrid
            // Ein neuer Filterstand ist ein komplett neuer Datensatz - der
            // Remount setzt Seite, geladene Zeilen und das eingebaute
            // Suchfeld sauber zurück (siehe Erläuterung oben zum
            // Signatur-Vergleich in DataGrid, der externe URL-Wechsel sonst
            // nicht als neue Anfrage erkennen würde).
            key={dataUrl}
            columns={columns}
            getRowId={(row) => row.article_number}
            initialSorting={initialSorting}
            onStateChange={handleStateChange}
            lockedColumnIds={["article_number", "name"]}
            groupingOptions={groupingOptions}
            emptyLabel="Keine Artikel für den gewählten Zeitraum und Filter gefunden."
            dataUrl={dataUrl}
            exportUrl={exportUrl}
            itemLabel="Artikel"
            itemLabelDative="Artikeln"
            defaultPageSize={100}
            compactHeader
        />
    );
}
